import { useState, useCallback, useEffect, useRef } from 'react';
import { galleries } from '../services/api';
import { useHedera } from '../contexts/HederaContext';

export const useGalleryAssets = (galleryId, initialAssets = {}, onAssetsUpdate) => {
    const { accountId } = useHedera();
    const [localAssets, setLocalAssets] = useState(initialAssets);
    const [pendingUpdates, setPendingUpdates] = useState(new Map());
    const updateTimeoutRef = useRef(null);
    const lastUpdateRef = useRef({});
    const initialAssetsRef = useRef(initialAssets);

    // Track current transforms separately from lastUpdateRef
    const currentTransformsRef = useRef({});

    // Handle initialAssets changes
    useEffect(() => {
        const hasChanged = JSON.stringify(initialAssetsRef.current) !== JSON.stringify(initialAssets);
        if (hasChanged) {
            console.log('Initial assets changed:', {
                old: initialAssetsRef.current,
                new: initialAssets,
                currentTransforms: currentTransformsRef.current
            });
            
            initialAssetsRef.current = initialAssets;

            // Merge new assets with current transforms
            const mergedAssets = Object.entries(initialAssets || {}).reduce((acc, [id, asset]) => {
                // Skip null/undefined assets
                if (!asset) {
                    console.log('Skipping null asset:', id);
                    return acc;
                }
                
                const currentTransform = currentTransformsRef.current[id];
                console.log('Processing asset:', {
                    id,
                    asset,
                    currentTransform
                });

                // Ensure we have valid transforms
                const position = currentTransform?.position || asset?.position || [0, 0, 0];
                const rotation = currentTransform?.rotation || asset?.rotation || [0, 0, 0];
                const scale = currentTransform?.scale || asset?.scale || [1, 1, 1];

                acc[id] = {
                    ...asset,
                    position,
                    rotation,
                    scale
                };
                return acc;
            }, {});

            console.log('Setting merged assets:', mergedAssets);
            setLocalAssets(mergedAssets);
        }
    }, [initialAssets]);

    // Update transforms when localAssets changes
    useEffect(() => {
        Object.entries(localAssets).forEach(([id, asset]) => {
            if (!asset) return;  // Skip null assets
            
            if (asset.position || asset.rotation || asset.scale) {
                currentTransformsRef.current[id] = {
                    position: asset.position || [0, 0, 0],
                    rotation: asset.rotation || [0, 0, 0],
                    scale: asset.scale || [1, 1, 1]
                };
            }
        });
    }, [localAssets]);

    const updateAssetTransform = useCallback(async (spotId, updatedAsset) => {
        if (!galleryId || !accountId || !spotId || !updatedAsset) {
            console.error('Missing required params:', { galleryId, accountId, spotId, updatedAsset });
            return;
        }

        console.log('Starting asset transform update:', {
            spotId,
            currentTransforms: JSON.stringify(currentTransformsRef.current, null, 2),
            updatedAsset: JSON.stringify(updatedAsset, null, 2),
            existingLocalAssets: JSON.stringify(localAssets, null, 2)
        });

        try {
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }

            // Update current transform immediately
            currentTransformsRef.current[spotId] = {
                position: updatedAsset.position || [0, 0, 0],
                rotation: updatedAsset.rotation || [0, 0, 0],
                scale: updatedAsset.scale || [1, 1, 1]
            };

            console.log('Updated current transforms:', {
                spotId,
                newTransforms: JSON.stringify(currentTransformsRef.current[spotId], null, 2)
            });

            // Create updated assets object
            const updatedAssets = {
                ...localAssets,
                [spotId]: updatedAsset
            };

            console.log('Preparing to send to server:', {
                spotId,
                updatedAssets: JSON.stringify(updatedAssets, null, 2)
            });

            // Optimistic update
            setLocalAssets(updatedAssets);

            // Debounce server update
            await new Promise(resolve => {
                updateTimeoutRef.current = setTimeout(resolve, 100);
            });

            // Send to server
            const response = await galleries.updateAssets(galleryId, updatedAssets, accountId);

            console.log('Received server response:', {
                spotId,
                responseAssets: JSON.stringify(response.assets, null, 2)
            });

            if (response.assets) {
                // Merge response with current transforms
                const mergedAssets = Object.entries(response.assets || {}).reduce((acc, [id, asset]) => {
                    if (!asset) {
                        console.log('Skipping null response asset:', id);
                        return acc;
                    }
                    
                    const currentTransform = currentTransformsRef.current[id];
                    console.log('Merging asset response:', {
                        id,
                        asset: JSON.stringify(asset, null, 2),
                        currentTransform: JSON.stringify(currentTransform, null, 2)
                    });

                    acc[id] = {
                        ...asset,
                        position: currentTransform?.position || asset?.position || [0, 0, 0],
                        rotation: currentTransform?.rotation || asset?.rotation || [0, 0, 0],
                        scale: currentTransform?.scale || asset?.scale || [1, 1, 1]
                    };
                    return acc;
                }, {});

                console.log('Final merged assets:', {
                    spotId,
                    mergedAssets: JSON.stringify(mergedAssets, null, 2)
                });

                setLocalAssets(mergedAssets);
                onAssetsUpdate?.(mergedAssets);
            }

            return response.assets;
        } catch (error) {
            console.error('Failed to update asset transform:', error);
            return null;
        }
    }, [galleryId, accountId, localAssets, galleries, onAssetsUpdate]);

    return {
        localAssets,
        updateAssetTransform,
        hasPendingUpdates: pendingUpdates.size > 0
    };
}; 